import React, { FC } from "react"
import { Helmet } from "react-helmet"
import { graphql, useStaticQuery } from "gatsby"
import { getSrc } from "gatsby-plugin-image"
import type { SeoProps } from "./types"

const activeEnv = process.env.GATSBY_ACTIVE_ENV || process.env.NODE_ENV || "development"

const Seo: FC<SeoProps> = ({
    title,
    description = "",
    metaImage,
    index = true,
    children,
    location,
    frontPage
}) => {
    const locale = "nl"
    const { site } = useStaticQuery(
        graphql`
            query {
                site {
                    siteMetadata {
                        title
                        description
                        author
                        siteUrl
                        legalName
                    }
                }
            }
        `
    )
    const ldJson = {
        "@context": "https://schema.org/",
        "@type": "Organization",
        name: site?.siteMetadata?.author,
        legalName: site?.siteMetadata?.legalName,
        url: site?.siteMetadata?.siteUrl,
        // "description": "", // Get from default seo settings
        // "contactPoint": {
        //     "@type": "ContactPoint",
        //     "telephone": "+31850607337",
        //     "contactType": "customer service"
        // },
        // "logo": `${site?.siteMetadata?.siteUrl}/images/bold-logo.svg`,
        sameAs: [
            // "https://www.youtube.com/channel/UC8iAb15P42QUhMWSyu4Z-MA",
            // "https://www.instagram.com/boldsmartlock/",
            // "https://www.facebook.com/BoldSmartLock",
            // "https://www.linkedin.com/company/boldsmartlock/"
        ]
        // "address": {
        //     "@type": "PostalAddress",
        //     "streetAddress": "Streetname 123",
        //     "addressLocality": "Cityname",
        //     "addressRegion": "Regionname",
        //     "postalCode": "1234 AB",
        //     "addressCountry": "The Netherlands"
        // }
    }
    const metaTitle = title || site.siteMetadata?.title
    const metaDescription = description || site.siteMetadata.description
    const metaImageUrl = getSrc(metaImage?.file)

    return (
        <Helmet
            htmlAttributes={{
                lang: locale
            }}
            title={metaTitle}
        >
            <script type="application/ld+json">
                {JSON.stringify(ldJson)}
            </script>
            {frontPage
                && <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "http://schema.org",
                        "@type": "WebSite",
                        name: site?.siteMetadata?.author,
                        url: site?.siteMetadata?.siteUrl
                    })}
                </script>}
            {(index === false || activeEnv !== "production") && <meta name="robots" content="noindex" />}
            <meta name="description" content={metaDescription} />
            <link rel="canonical" href={`${site.siteMetadata?.siteUrl}${location?.pathname}`} />
            <meta property="og:locale" content={locale} />
            <meta property="og:type" content="website" />
            <meta property="og:title" content={metaTitle} />
            <meta property="og:description" content={metaDescription} />
            <meta property="og:url" content={site.siteMetadata?.siteUrl} />
            <meta property="og:site_name" content={site.siteMetadata?.author} />
            {!!metaImage && <meta property="og:image" content={`${site.siteMetadata?.siteUrl}${metaImageUrl}`} />}
            {!!metaImage
                && <meta property="og:image:secure_url" content={`${site.siteMetadata?.siteUrl}${metaImageUrl}`} />}
            {!!metaImage && <meta property="og:image:width" content={`${metaImage.width}`} />}
            {!!metaImage && <meta property="og:image:height" content={`${metaImage.height}`} />}
            <meta name="twitter:card" content="summary" />
            <meta name="twitter:title" content={metaTitle} />
            <meta name="twitter:description" content={metaDescription} />
            {children}
        </Helmet>
    )
}

export default Seo
